import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Easing, Modal, Dimensions, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import LogoSvg from '../LogoSvg';
import GradientButton from './GradientButton';
import { useNavigation } from '../context/NavigationContext';

// Create animated version of LogoSvg
const AnimatedLogoSvg = Animated.createAnimatedComponent(LogoSvg);

const BetSlipSubmissionScreen = ({ onComplete, onSave, selectedLegs = [] }) => {
    const { navigateTo } = useNavigation();
    const [isLoading, setIsLoading] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const backgroundOpacity = useRef(new Animated.Value(0)).current;
    const textOpacity = useRef(new Animated.Value(0)).current;
    const successOpacity = useRef(new Animated.Value(0)).current;
    
    const screenWidth = Dimensions.get('window').width;
    const screenHeight = Dimensions.get('window').height;

    // Get the first leg's deep link or fallback to first available sportsbook homepage
    const getDeepLink = () => {
        // Try to get deep link from first leg
        if (selectedLegs.length > 0) {
            const firstLeg = selectedLegs[0];
            const urls = firstLeg?.raw?.urls || {};
            const deeplink = urls.offer || urls.deeplink || urls.event || urls.market || null;
            if (deeplink) return deeplink;
            
            // Fallback: if no deep link but we have a sportsbook name, use homepage
            const bookName = firstLeg?.raw?.sportsbook || firstLeg?.raw?.book || null;
            const bookLinks = {
                DraftKings: 'https://sportsbook.draftkings.com/',
                FanDuel: 'https://sportsbook.fanduel.com/',
                Caesars: 'https://www.caesars.com/sportsbook',
                bet365: 'https://www.bet365.com/',
                BetMGM: 'https://sports.betmgm.com/en/sports',
                PointsBet: 'https://pointsbet.com/'
            };
            if (bookName && bookLinks[bookName]) return bookLinks[bookName];
        }
        return null;
    };

    useEffect(() => {
        // Start the loading animation
        startLoadingAnimation();
    }, []);

    const startLoadingAnimation = () => {
        // Create alternating black/white animation
        const createAlternatingAnimation = () => {
            return Animated.loop(
                Animated.sequence([
                    Animated.timing(backgroundOpacity, {
                        toValue: 1,
                        duration: 1000,
                        easing: Easing.inOut(Easing.cubic),
                        useNativeDriver: false,
                    }),
                    Animated.timing(backgroundOpacity, {
                        toValue: 0,
                        duration: 1000,
                        easing: Easing.inOut(Easing.cubic),
                        useNativeDriver: false,
                    }),
                ])
            );
        };

        // Start alternating animation
        createAlternatingAnimation().start();

        // Show text immediately and keep it visible
        Animated.timing(textOpacity, {
            toValue: 1,
            duration: 500,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
        }).start();

        // Show success screen after 4 seconds
        setTimeout(() => {
            setIsLoading(false);
            setShowSuccess(true);
            Animated.timing(successOpacity, {
                toValue: 1,
                duration: 800,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: false,
            }).start();
        }, 4000);
    };

    const handleViewSportsbook = () => {
        try {
            const url = getDeepLink();
            if (url && Platform.OS === 'web') {
                window.open(url, '_blank');
            }
        } catch (err) {
            console.error('Failed to open sportsbook:', err);
        }
    };

    const handleSaveAndGoHome = async () => {
        // Save the bet slip to Firestore
        if (onSave) await onSave();
        // Clear submission screen and navigate home
        if (onComplete) onComplete();
        navigateTo('Home');
    };

    const handleExitWithoutSaving = () => {
        // Just navigate home without saving
        if (onComplete) onComplete();
        navigateTo('Home');
    };

    if (showSuccess) {
        return (
            <Modal visible={true} transparent={false} animationType="fade">
                <View style={[styles.successOuterContainer, { height: screenHeight }]}>
                    <LinearGradient
                        colors={['#749A77', '#2B4E2D']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.successGradientBackground}
                    >
                        <View style={[styles.successContainer, { width: screenWidth, height: screenHeight }]}>
                            <Animated.View style={[styles.successContent, { opacity: successOpacity }]}>
                                <View style={styles.successHeader}>
                                    <View style={styles.successTitleContainer}>
                                        <View style={[styles.logoContainer, { transform: [{ rotate: '103deg' }] }]}>
                                            <LogoSvg 
                                                width={32} 
                                                height={32} 
                                                color="rgba(255, 255, 255, 0.80)" 
                                                spin={false}
                                            />
                                        </View>
                                        <Text style={styles.successTitle}>Success</Text>
                                        <View style={[styles.logoContainer, { transform: [{ rotate: '-150deg' }] }]}>
                                            <LogoSvg 
                                                width={32} 
                                                height={32} 
                                                color="rgba(255, 255, 255, 0.80)" 
                                                spin={false}
                                            />
                                        </View>
                                    </View>
                                </View>
                                
                                <View style={styles.successSubtitleContainer}>
                                    <Text style={styles.successSubtitle}>
                                        Your Bet Slip was created Successfully!
                                    </Text>
                                </View>
                                
                                <View style={styles.successActions}>
                                    <GradientButton
                                        text="View on Sportsbook"
                                        onPress={handleViewSportsbook}
                                        style={styles.successButton}
                                        textStyle={styles.successButtonText}
                                        gradientColors={["#D2D2D2", "#D2D2D2"]}
                                    />
                                </View>
                                
                                <View style={styles.successActions}>
                                    <GradientButton
                                        text="Save and Go Home"
                                        onPress={handleSaveAndGoHome}
                                        style={styles.successButton}
                                        textStyle={styles.successButtonText}
                                        gradientColors={["#D2D2D2", "#D2D2D2"]}
                                    />
                                    <GradientButton
                                        text="Exit without Saving"
                                        onPress={handleExitWithoutSaving}
                                        style={[styles.successButton, styles.exitButton]}
                                        textStyle={styles.successButtonText}
                                        gradientColors={["#D2D2D2", "#D2D2D2"]}
                                    />
                                </View>
                            </Animated.View>
                        </View>
                    </LinearGradient>
                </View>
            </Modal>
        );
    }

    return (
        <Modal visible={true} transparent={false} animationType="fade">
            <View style={[styles.container, { width: screenWidth, height: screenHeight }]}>
                <Animated.View 
                    style={[
                        styles.backgroundOverlay,
                        {
                            backgroundColor: backgroundOpacity.interpolate({
                                inputRange: [0, 1],
                                outputRange: ['rgba(0, 0, 0, 1)', 'rgba(255, 255, 255, 1)'],
                            }),
                        }
                    ]}
                />
                
                <View style={styles.content}>
                    <Animated.View style={[styles.textContainer, { opacity: textOpacity }]}>
                        <View style={styles.titleContainer}>
                            <View style={styles.logoContainer}>
                                <AnimatedLogoSvg 
                                    width={32} 
                                    height={32} 
                                    color={backgroundOpacity.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                    })}
                                    spin={true}
                                />
                            </View>
                            <Animated.Text style={[
                                styles.title,
                                {
                                    color: backgroundOpacity.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                    }),
                                }
                            ]}>
                                BET OPTIMIZED
                            </Animated.Text>
                            <View style={styles.logoContainer}>
                                <AnimatedLogoSvg 
                                    width={32} 
                                    height={32} 
                                    color={backgroundOpacity.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                    })}
                                    spin={true}
                                />
                            </View>
                        </View>
                    </Animated.View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingLeft: 48,
        paddingRight: 48,
        backgroundColor: 'white',
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'black',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    backgroundOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    content: {
        alignSelf: 'stretch',
        paddingTop: 16,
        paddingBottom: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 303,
    },
    textContainer: {
        height: 32,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 10,
        flexDirection: 'row',
    },
    titleContainer: {
        height: 32,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 10,
        flexDirection: 'row',
    },
    title: {
        fontSize: 40,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        lineHeight: 40,
    },
    logoContainer: {
        width: 32,
        height: 32,
        position: 'relative',
        overflow: 'visible',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconLeft: {
        width: 32,
        height: 32,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'transparent',
    },
    iconRight: {
        width: 32,
        height: 32,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'transparent',
    },
    successOuterContainer: {
        flex: 1,
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    successGradientBackground: {
        flex: 1,
        width: '100%',
    },
    successContainer: {
        flex: 1,
        paddingLeft: 48,
        paddingRight: 48,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'black',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
    },
    successContent: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
    },
    successHeader: {
        alignSelf: 'stretch',
        paddingTop: 16,
        paddingBottom: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 303,
    },
    successTitleContainer: {
        height: 32,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 10,
        flexDirection: 'row',
    },
    successTitle: {
        fontSize: 40,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        lineHeight: 40,
        color: 'rgba(255, 255, 255, 0.80)',
    },
    successSubtitleContainer: {
        alignSelf: 'stretch',
        paddingTop: 16,
        paddingBottom: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 303,
    },
    successSubtitle: {
        fontSize: 32,
        fontFamily: 'Slate',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: 32,
        color: 'rgba(255, 255, 255, 0.80)',
        textAlign: 'center',
    },
    successActions: {
        width: 319,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
        flexDirection: 'row',
    },
    successButton: {
        backgroundColor: 'rgba(116, 154, 119, 0.20)',
        borderColor: 'rgba(116, 154, 119, 0.30)',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.10,
        shadowRadius: 4,
        elevation: 4,
    },
    exitButton: {
        backgroundColor: 'rgba(255, 78, 78, 0.15)',
        borderColor: 'rgba(255, 78, 78, 0.25)',
    },
    successButtonText: {
        color: '#D2D2D2',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
});

export default BetSlipSubmissionScreen;
